import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, MessageSquare, Check, X, Send, Loader2, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { supportService, SupportChat, SupportMessage, supabase } from '@/integrations/supabase/client';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { validateUserSession } from '@/lib/utils/uuidValidation';

export const AdminChats = () => {
  const { profile, user, isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Enhanced validation logging
  useEffect(() => {
    console.log("AdminChats - Auth validation:", {
      isAuthenticated,
      hasUser: !!user,
      userId: user?.id || 'missing',
      userIdValid: user?.id ? validateUserSession(user) : false,
      hasProfile: !!profile,
      profileId: profile?.id || 'missing',
      role: profile?.role || 'missing',
      isAdmin: profile?.role === 'admin'
    });
  }, [isAuthenticated, user, profile]);
  
  // Enhanced chats query with better error handling
  const { data: chats = [], isLoading: chatsLoading, error: chatsError, refetch } = useQuery({
    queryKey: ['adminChats'],
    queryFn: async () => {
      console.log("AdminChats query - Starting...");
      
      // Validate user session first
      if (!validateUserSession(user)) {
        throw new Error('Invalid user session - user ID is missing or invalid');
      }
      
      if (!profile || profile.role !== 'admin') {
        throw new Error('User is not an admin');
      }
      
      try {
        const { data, error } = await supportService.getChats();
        
        if (error) {
          console.error("AdminChats query - Service error:", error);
          throw error;
        }
        
        console.log(`AdminChats query - Retrieved ${data?.length || 0} chats`);
        
        // Enhanced user info fetching with validation
        const chatsWithUserInfo = await Promise.all((data || []).map(async (chat: SupportChat) => {
          try {
            // Validate chat user_id
            if (!chat.user_id || chat.user_id === 'null') {
              console.warn(`AdminChats - Chat ${chat.id} has invalid user_id: ${chat.user_id}`);
              return {
                ...chat,
                user_email: 'Invalid User ID',
                user_name: 'Invalid User ID'
              };
            }
            
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('email, username, full_name')
              .eq('id', chat.user_id)
              .maybeSingle();
            
            if (profileError) {
              console.warn(`AdminChats - Profile query error for user ${chat.user_id}:`, profileError);
            }
            
            return {
              ...chat,
              user_email: profileData?.email || 'Unknown Email',
              user_name: profileData?.full_name || profileData?.username || 'Unknown User'
            };
          } catch (error) {
            console.error(`AdminChats - Error processing chat ${chat.id}:`, error);
            return {
              ...chat,
              user_email: 'Error Loading',
              user_name: 'Error Loading'
            };
          }
        }));
        
        console.log("AdminChats query - Successfully processed all chats");
        return chatsWithUserInfo;
      } catch (error) {
        console.error("AdminChats query - Fatal error:", error);
        throw error;
      }
    },
    enabled: !!user && !!profile && profile.role === 'admin' && validateUserSession(user),
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
  
  // Enhanced messages query with validation
  const { data: messages = [], isLoading: messagesLoading, refetch: refetchMessages } = useQuery({
    queryKey: ['adminChatMessages', selectedChat],
    queryFn: async () => {
      if (!selectedChat) {
        console.log("AdminChats messages - No chat selected");
        return [];
      }
      
      console.log(`AdminChats messages - Fetching for chat: ${selectedChat}`);
      
      const { data, error } = await supportService.getChatMessages(selectedChat);
      
      if (error) {
        console.error("AdminChats messages - Service error:", error);
        throw error;
      }
      
      console.log(`AdminChats messages - Retrieved ${data?.length || 0} messages`);
      return data || [];
    },
    enabled: !!selectedChat,
    retry: 2,
    retryDelay: 1000,
  });
  
  useEffect(() => {
    // Scroll to bottom when messages change
    const messagesContainer = document.getElementById('admin-messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages]);
  
  const filteredChats = chats.filter((chat: any) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      chat.user_email?.toLowerCase().includes(query) ||
      chat.user_name?.toLowerCase().includes(query) ||
      chat.id.toLowerCase().includes(query)
    );
  });
  
  const activeChats = filteredChats.filter((chat: any) => !chat.is_resolved);
  const resolvedChats = filteredChats.filter((chat: any) => chat.is_resolved);
  
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedChat || !newMessage.trim()) {
      console.warn("AdminChats - Reply aborted: missing chat ID or message");
      return;
    }
    
    // Validate user session before sending
    if (!validateUserSession(user)) {
      console.error("AdminChats - Reply aborted: invalid user session");
      return;
    }
    
    try {
      setSending(true);
      console.log(`AdminChats - Sending reply to chat: ${selectedChat}`);
      
      const { error } = await supportService.createMessage({
        chat_id: selectedChat,
        sender_type: 'admin',
        content: newMessage.trim()
      });
      
      if (error) {
        throw error;
      }
      
      setNewMessage('');
      refetchMessages();
      console.log("AdminChats - Reply sent successfully");
    } catch (error) {
      console.error('AdminChats - Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };
  
  const toggleChatResolution = async (chatId: string, resolved: boolean) => {
    if (!validateUserSession(user)) {
      console.error("AdminChats - Resolution toggle aborted: invalid user session");
      return;
    }
    
    try {
      console.log(`AdminChats - Toggling resolution for chat ${chatId} to ${resolved}`);
      
      const { error } = await supportService.updateChatResolution(chatId, resolved);
      
      if (error) {
        throw error;
      }
      
      refetch();
      
      if (selectedChat === chatId && resolved) {
        setSelectedChat(null);
      }
      
      console.log("AdminChats - Resolution toggled successfully");
    } catch (error) {
      console.error('AdminChats - Error updating chat resolution:', error);
    }
  };
  
  // Enhanced error handling
  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium">Not Authenticated</h3>
            <p className="text-gray-500">Please log in to access admin features.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!validateUserSession(user)) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium">Invalid Session</h3>
            <p className="text-gray-500">Your session is invalid. Please log out and log back in.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!profile || profile.role !== 'admin') {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium">Access Denied</h3>
            <p className="text-gray-500">You don't have permission to view this page.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (chatsError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-600">Error Loading Chats</h3>
            <p className="text-gray-500 mb-4">{chatsError.message}</p>
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="flex flex-col h-[calc(100vh-80px)]">
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardHeader className="border-b">
          <CardTitle>Bisedat e Klientëve</CardTitle>
          <CardDescription>Menaxhoni bisedat me përdoruesit tuaj</CardDescription>
        </CardHeader>
        
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Chat List */}
          <div className={`${selectedChat ? 'hidden md:flex md:w-1/3 lg:w-1/4' : 'flex-1'} flex flex-col border-r`}>
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Kërko biseda..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <Tabs defaultValue="active" className="flex-1 flex flex-col h-full">
              <TabsList className="grid grid-cols-2 m-2">
                <TabsTrigger value="active" className="text-xs py-1">Aktive ({activeChats.length})</TabsTrigger>
                <TabsTrigger value="resolved" className="text-xs py-1">Të zgjidhura ({resolvedChats.length})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="active" className="flex-1 overflow-y-auto">
                {chatsLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="animate-spin h-5 w-5 mx-auto" />
                    <p className="text-sm text-gray-500 mt-2">Duke ngarkuar bisedat...</p>
                  </div>
                ) : activeChats.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-500">Nuk ka biseda aktive</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {activeChats.map((chat: any) => (
                      <button
                        key={chat.id}
                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                          selectedChat === chat.id ? 'bg-gray-100' : ''
                        }`}
                        onClick={() => setSelectedChat(chat.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{chat.user_name || 'Përdorues pa emër'}</p>
                            <p className="text-xs text-gray-500 truncate">{chat.user_email || 'Email i panjohur'}</p>
                          </div>
                          <Badge variant="outline" className="ml-2 flex-shrink-0 bg-green-100 text-green-800 text-xs">
                            Aktive
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {format(new Date(chat.updated_at), 'dd/MM/yy HH:mm')}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="resolved" className="flex-1 overflow-y-auto">
                {chatsLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="animate-spin h-5 w-5 mx-auto" />
                    <p className="text-sm text-gray-500 mt-2">Duke ngarkuar bisedat...</p>
                  </div>
                ) : resolvedChats.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-500">Nuk ka biseda të zgjidhura</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {resolvedChats.map((chat: any) => (
                      <button
                        key={chat.id}
                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                          selectedChat === chat.id ? 'bg-gray-100' : ''
                        }`}
                        onClick={() => setSelectedChat(chat.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{chat.user_name || 'Përdorues pa emër'}</p>
                            <p className="text-xs text-gray-500 truncate">{chat.user_email || 'Email i panjohur'}</p>
                          </div>
                          <Badge variant="outline" className="ml-2 flex-shrink-0 bg-gray-100 text-gray-800 text-xs">
                            E zgjidhur
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {format(new Date(chat.updated_at), 'dd/MM/yy HH:mm')}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Chat Window */}
          <div className={`${!selectedChat ? 'hidden md:flex' : 'flex'} flex-col flex-1 border-t md:border-t-0`}>
            {selectedChat ? (
              <>
                <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
                  <div className="flex items-center">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="md:hidden mr-2"
                      onClick={() => setSelectedChat(null)}
                    >
                      <ArrowLeft size={18} />
                    </Button>
                    <div>
                      <h3 className="font-medium">
                        Biseda #{selectedChat.slice(0, 6)}...
                      </h3>
                      <p className="text-xs text-gray-500">
                        {activeChats.find((c: any) => c.id === selectedChat)?.user_name || 
                        resolvedChats.find((c: any) => c.id === selectedChat)?.user_name || 'Përdorues'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {activeChats.some((c: any) => c.id === selectedChat) ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="gap-1 text-green-600 text-xs h-8"
                        onClick={() => toggleChatResolution(selectedChat, true)}
                      >
                        <Check size={14} />
                        <span className="hidden sm:inline">Shënoje si të zgjidhur</span>
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="gap-1 text-xs h-8"
                        onClick={() => toggleChatResolution(selectedChat, false)}
                      >
                        <X size={14} />
                        <span className="hidden sm:inline">Rihap</span>
                      </Button>
                    )}
                  </div>
                </div>
                
                <div 
                  id="admin-messages-container" 
                  className="flex-1 overflow-y-auto p-3 space-y-3"
                >
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <MessageSquare className="h-12 w-12 mb-4 opacity-30" />
                      <h3 className="text-lg font-medium mb-1">Zgjidh një bisedë</h3>
                      <p className="text-sm max-w-md">Zgjidh një bisedë nga lista për të parë dhe përgjigjur mesazheve</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div 
                        key={message.id}
                        className={`flex ${
                          message.sender_type === 'user' ? 'justify-start' : 'justify-end'
                        }`}
                      >
                        <div className={`max-w-[85%] md:max-w-[70%] rounded-lg p-3 ${
                          message.sender_type === 'user' 
                            ? 'bg-gray-100' 
                            : message.sender_type === 'admin' 
                              ? 'bg-brand-100 text-brand-900' 
                              : 'bg-gray-200'
                        }`}>
                          <div className="text-sm">{message.content}</div>
                          <div className="text-xs text-right mt-1 opacity-70">
                            {format(new Date(message.created_at), 'HH:mm')}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="border-t p-3">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Shkruani mesazhin tuaj..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="flex-1"
                    />
                    <Button type="submit" disabled={!newMessage.trim()}>
                      <Send size={16} className="mr-1" />
                      <span className="hidden sm:inline">Dërgo</span>
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4 text-center">
                <MessageSquare className="h-12 w-12 mb-4 opacity-30" />
                <h3 className="text-lg font-medium mb-1">Zgjidh një bisedë</h3>
                <p className="text-sm max-w-md">Zgjidh një bisedë nga lista për të parë dhe përgjigjur mesazheve</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
